<template>
  <div class="admin-container">
    <div v-if="notAuthorized" class="not-authorized">
      <div class="login-card">
        <h1>Not Authorized</h1>
        <p>You have not been granted admin access</p>
        <p>
          If you believe this is an error, please contact your administrator.
        </p>
      </div>
    </div>
    <!-- Admin Login Form -->
    <div v-else-if="!isAuthenticated" class="admin-login">
      <div class="login-card">
        <h1>Admin Access Required</h1>
        <p>Please enter your admin email to access the survey dashboard.</p>

        <div class="login-form">
          <div class="form-group">
            <label for="adminEmail">Admin Email</label>
            <input
              id="adminEmail"
              v-model="adminEmail"
              type="email"
              placeholder="Enter admin email"
              class="login-input"
              :disabled="isLoading"
            />
          </div>

          <div v-if="message" :class="`message ${messageType}`">
            {{ message }}
          </div>

          <button
            @click="handleAdminLogin"
            class="login-btn"
            :disabled="!adminEmail || isLoading"
          >
            {{ isLoading ? "Sending..." : "Send Admin Login Link" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Admin Dashboard -->
    <div v-else class="admin-dashboard">
      <div class="admin-header">
        <h1>Survey Admin Dashboard</h1>
        <div class="admin-controls">
          <UserProfileDropdown
            :userName="
              currentAdmin?.displayName || currentAdmin?.email || 'Admin'
            "
            :userEmail="currentAdmin?.email || ''"
            @sign-out="handleSignOut"
            @profile="handleProfile"
            @settings="handleSettings"
            @help="handleHelp"
          />
        </div>
      </div>

      <!-- Dashboard Messages -->
      <div v-if="message" :class="`message ${messageType}`">
        {{ message }}
      </div>

      <!-- Global Loading Overlay -->
      <div
        v-if="isLoading || isReturningFromLogin || initialDashboardLoading"
        class="loading-overlay"
      >
        <div class="loading-content">
          <div class="loading-spinner-large"></div>
          <h3 v-if="isReturningFromLogin">Welcome Back!</h3>
          <h3 v-else>Loading Admin Dashboard...</h3>
          <p v-if="isReturningFromLogin">Setting up your session...</p>
          <p v-else>Loading data...</p>
        </div>
      </div>

      <!-- Tab Navigation with Animated Toggle -->
      <div class="tab-navigation">
        <div class="tab-toggle-container">
          <div class="tab-toggle-slider" :style="sliderStyle"></div>
          <button
            @click="handleTabClick('survey')"
            :class="`tab-btn ${activeTab === 'survey' ? 'active' : ''}`"
            data-tab="survey"
          >
            Survey Responses
          </button>
          <button
            @click="handleTabClick('auth')"
            :class="`tab-btn ${activeTab === 'auth' ? 'active' : ''}`"
            data-tab="auth"
          >
            Registered Users
          </button>
          <button
            @click="handleTabClick('admins')"
            :class="`tab-btn ${activeTab === 'admins' ? 'active' : ''}`"
            data-tab="admins"
          >
            Admin Management
          </button>
          <button
            @click="handleTabClick('issues')"
            :class="`tab-btn ${activeTab === 'issues' ? 'active' : ''}`"
            data-tab="issues"
          >
            Reported Issues
          </button>
        </div>
      </div>

      <!-- Survey Users Tab -->
      <div v-if="activeTab === 'survey'" class="tab-content">
        <!-- Tab Header with Title and Export Button -->
        <div class="tab-header">
          <div class="tab-title">
            <h2>Survey Responses</h2>
          </div>
          <div class="tab-actions">
            <button
              @click="exportToCSV"
              class="export-btn"
              :disabled="!users.length"
            >
              Export Survey Responses to CSV
            </button>
          </div>
        </div>

        <!-- Stats Overview -->
        <div class="stats-grid">
          <div class="stat-card">
            <h3>Total Users</h3>
            <p class="stat-number" v-if="!loading">{{ users.length }}</p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
          <div class="stat-card">
            <h3>Completed Surveys</h3>
            <p class="stat-number" v-if="!loading">{{ completedSurveys }}</p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
          <div class="stat-card">
            <h3>Partial Surveys</h3>
            <p class="stat-number" v-if="!loading">{{ partialSurveys }}</p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
          <div class="stat-card">
            <h3>Today's Signups</h3>
            <p class="stat-number" v-if="!loading">{{ todaySignups }}</p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
        </div>

        <!-- Search and Filter -->
        <div class="search-filter-section">
          <div class="search-box">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name or email..."
              class="search-input"
            />
          </div>
          <div class="filter-controls">
            <select v-model="statusFilter" class="filter-select">
              <option value="">All Users</option>
              <option value="completed">Completed Surveys</option>
              <option value="partial">Partial Surveys</option>
              <option value="new">New Users</option>
            </select>
            <select v-model="dateFilter" class="filter-select">
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>

        <!-- Users Table -->
        <div class="table-container">
          <table class="users-table">
            <thead>
              <tr>
                <th @click="sortBy('firstName')" class="sortable">
                  Name
                  <span v-if="sortField === 'firstName'" class="sort-indicator">
                    {{ sortDirection === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th @click="sortBy('email')" class="sortable">
                  Email
                  <span v-if="sortField === 'email'" class="sort-indicator">
                    {{ sortDirection === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th @click="sortBy('createdAt')" class="sortable">
                  Signup Date
                  <span v-if="sortField === 'createdAt'" class="sort-indicator">
                    {{ sortDirection === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th>Survey Status</th>
                <th @click="sortBy('lastUpdated')" class="sortable">
                  Last Activity
                  <span
                    v-if="sortField === 'lastUpdated'"
                    class="sort-indicator"
                  >
                    {{ sortDirection === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="user-row">
                <td colspan="6" style="text-align: center; padding: 2rem">
                  <div class="loading-spinner">Loading survey responses...</div>
                </td>
              </tr>
              <tr v-else-if="filteredUsers.length === 0" class="user-row">
                <td
                  colspan="6"
                  style="
                    text-align: center;
                    padding: 2rem;
                    color: #6c757d;
                    font-style: italic;
                  "
                >
                  No survey responses
                </td>
              </tr>
              <tr
                v-else
                v-for="user in paginatedUsers"
                :key="user.uid"
                class="user-row"
              >
                <td>
                  <div class="user-name">
                    {{ user.firstName }} {{ user.lastName }}
                  </div>
                </td>
                <td>{{ user.email }}</td>
                <td>{{ formatDate(user.createdAt) }}</td>
                <td>
                  <span :class="`status-badge ${getSurveyStatus(user).class}`">
                    {{ getSurveyStatus(user).text }}
                  </span>
                </td>
                <td>{{ formatDate(user.lastUpdated || user.createdAt) }}</td>
                <td>
                  <div class="action-buttons">
                    <button @click="viewUserDetails(user)" class="view-btn">
                      View Details
                    </button>
                    <button
                      @click="deleteSurveyUser(user)"
                      class="delete-btn-small"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <!-- Pagination Controls -->
          <div
            v-if="totalPages > 1"
            class="pagination-controls"
            style="
              display: flex;
              justify-content: flex-end;
              align-items: center;
              gap: 1rem;
              margin: 1rem 0;
            "
          >
            <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
            >
              Previous
            </button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage === totalPages"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- Firebase Auth Users Tab -->
      <div v-if="activeTab === 'auth'" class="tab-content">
        <!-- Tab Header with Title and Export Button -->
        <div class="tab-header">
          <div class="tab-title">
            <h2>Registered Users</h2>
          </div>
          <div class="tab-actions">
            <button
              @click="exportToCSV"
              class="export-btn"
              :disabled="!authUsers.length"
            >
              Export Users to CSV
            </button>
          </div>
        </div>

        <!-- Auth Users Stats -->
        <div class="stats-grid">
          <div class="stat-card">
            <h3>Total Auth Users</h3>
            <p class="stat-number" v-if="!authLoading">
              {{ authUsers.length }}
            </p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
          <div class="stat-card">
            <h3>Email Verified</h3>
            <p class="stat-number" v-if="!authLoading">
              {{ emailVerifiedCount }}
            </p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
          <div class="stat-card">
            <h3>Disabled Users</h3>
            <p class="stat-number" v-if="!authLoading">
              {{ disabledUsersCount }}
            </p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
          <div class="stat-card">
            <h3>Recent Signups</h3>
            <p class="stat-number" v-if="!authLoading">
              {{ recentSignupsCount }}
            </p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
        </div>

        <!-- Auth Users Table -->
        <div class="table-container">
          <table class="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Email Verified</th>
                <th>Created At</th>
                <th>Last Sign In</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="authLoading" class="user-row">
                <td colspan="6" style="text-align: center; padding: 2rem">
                  <div class="loading-spinner">Loading auth users...</div>
                </td>
              </tr>
              <tr v-else-if="filteredAuthUsers.length === 0" class="user-row">
                <td
                  colspan="6"
                  style="
                    text-align: center;
                    padding: 2rem;
                    color: #6c757d;
                    font-style: italic;
                  "
                >
                  No auth users found.
                </td>
              </tr>
              <tr
                v-else
                v-for="authUser in filteredAuthUsers"
                :key="authUser.uid"
                class="user-row"
              >
                <td>{{ authUser.email }}</td>
                <td>
                  <span
                    :class="`status-badge ${
                      authUser.emailVerified ? 'verified' : 'unverified'
                    }`"
                  >
                    {{ authUser.emailVerified ? "Verified" : "Unverified" }}
                  </span>
                </td>
                <td>{{ formatDate(authUser.metadata.creationTime) }}</td>
                <td>{{ formatDate(authUser.metadata.lastSignInTime) }}</td>
                <td>
                  <span
                    :class="`status-badge ${
                      authUser.disabled ? 'disabled' : 'active'
                    }`"
                  >
                    {{ authUser.disabled ? "Disabled" : "Active" }}
                  </span>
                </td>
                <td>
                  <button
                    @click="deleteAuthUser(authUser)"
                    class="delete-btn-small"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Admin Management Tab -->
      <div v-if="activeTab === 'admins'" class="tab-content">
        <!-- Tab Header with Title -->
        <div class="tab-header">
          <div class="tab-title">
            <h2>Admin Management</h2>
          </div>
        </div>

        <!-- Admin Stats Overview -->
        <div class="stats-grid">
          <div class="stat-card">
            <h3>Total Admins</h3>
            <p class="stat-number" v-if="!adminLoading">
              {{ adminEmails.length }}
            </p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
          <div class="stat-card">
            <h3>Active Admins</h3>
            <p class="stat-number" v-if="!adminLoading">
              {{ adminEmails.length }}
            </p>
            <div v-else class="loading-spinner-small">Loading...</div>
          </div>
        </div>

        <!-- Admins Table (View Only) -->
        <div class="table-container">
          <table class="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="adminLoading" class="user-row">
                <td colspan="2" style="text-align: center; padding: 2rem">
                  Loading admin list...
                </td>
              </tr>
              <tr v-else-if="adminEmails.length === 0" class="user-row">
                <td
                  colspan="2"
                  style="
                    text-align: center;
                    padding: 2rem;
                    color: #6c757d;
                    font-style: italic;
                  "
                >
                  No admins found.
                </td>
              </tr>
              <tr
                v-else
                v-for="email in adminEmails"
                :key="email"
                class="user-row"
              >
                <td>{{ email }}</td>
                <td>
                  <span class="status-badge active">Active</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Messages (if any) -->
        <div
          v-if="adminMessage"
          :class="`message ${adminMessageType}`"
          style="margin-top: 1rem"
        >
          {{ adminMessage }}
        </div>
      </div>

      <!-- Reported Issues Tab -->
      <div v-if="activeTab === 'issues'" class="tab-content">
        <div class="tab-header">
          <div class="tab-title">
            <h2>Reported Issues</h2>
          </div>
        </div>
        <div class="table-container">
          <table class="users-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    :checked="
                      selectedIssueIds.length === issues.length &&
                      issues.length > 0
                    "
                    @change="selectAllIssues"
                  />
                </th>
                <th>Description</th>
                <th>Page</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="issuesLoading">
                <td colspan="5" style="text-align: center; padding: 2rem">
                  <div class="loading-spinner">Loading issues...</div>
                </td>
              </tr>
              <tr v-else-if="issues.length === 0">
                <td
                  colspan="5"
                  style="
                    text-align: center;
                    padding: 2rem;
                    color: #6c757d;
                    font-style: italic;
                  "
                >
                  No issues reported.
                </td>
              </tr>
              <tr v-else v-for="issue in issues" :key="issue.id">
                <td>
                  <input
                    type="checkbox"
                    :checked="selectedIssueIds.includes(issue.id)"
                    @change="() => toggleIssueSelection(issue.id)"
                  />
                </td>
                <td
                  style="
                    max-width: 400px;
                    white-space: pre-line;
                    overflow-wrap: anywhere;
                  "
                >
                  {{ issue.text }}
                </td>
                <td>{{ getIssuePage(issue) }}</td>
                <td>{{ formatDate(issue.debugInfo?.timestamp) }}</td>
                <td>
                  <button @click="viewIssueDetails(issue)" class="view-btn">
                    View Details
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          class="table-actions"
          v-if="activeTab === 'issues' && issues.length > 0"
        >
          <button
            @click="deleteSelectedIssues"
            :disabled="selectedIssueIds.length === 0"
            class="delete-btn-small"
          >
            Delete Selected
          </button>
        </div>
      </div>
      <!-- Issue Details Modal -->
      <div v-if="selectedIssue" class="modal-overlay" @click="closeIssueModal">
        <div class="modal-content issue-modal-content" @click.stop>
          <button
            @click="closeIssueModal"
            class="close-btn issue-modal-close-btn"
            aria-label="Close modal"
            style="position: absolute; top: 18px; right: 18px; z-index: 10"
          >
            <span>&times;</span>
          </button>
          <div class="modal-body">
            <div class="issue-details-row">
              <div class="issue-detail-col">
                <h3>Description</h3>
                <p style="white-space: pre-line">{{ selectedIssue.text }}</p>
              </div>
              <div class="issue-detail-col">
                <h3>Date</h3>
                <p>{{ formatDate(selectedIssue.debugInfo?.timestamp) }}</p>
              </div>
            </div>
            <div class="issue-debug-full">
              <pre class="debug-info-pre">{{
                formatDebugInfo(selectedIssue.debugInfo)
              }}</pre>
            </div>
          </div>
        </div>
      </div>

      <!-- User Details Modal -->
      <div v-if="selectedUser" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h2>
              User Details: {{ selectedUser.firstName }}
              {{ selectedUser.lastName }}
            </h2>
            <button
              @click="closeModal"
              class="close-btn"
              aria-label="Close modal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="user-info">
              <h3>Basic Information</h3>
              <p><strong>Email:</strong> {{ selectedUser.email }}</p>
              <p>
                <strong>Signup Date:</strong>
                {{ formatDate(selectedUser.createdAt) }}
              </p>
              <p>
                <strong>Last Updated:</strong>
                {{
                  formatDate(selectedUser.lastUpdated || selectedUser.createdAt)
                }}
              </p>
            </div>

            <div v-if="selectedUser.surveyData" class="survey-data">
              <h3>Survey Responses</h3>
              <div class="survey-sections">
                <div
                  v-for="(value, key) in selectedUser.surveyData"
                  :key="key"
                  class="survey-section"
                >
                  <h4>{{ formatFieldName(key) }}</h4>
                  <p>{{ formatFieldValue(value) }}</p>
                </div>
              </div>
            </div>

            <div v-else class="no-survey-data">
              <p>No survey data available for this user.</p>
            </div>

            <div class="modal-actions">
              <button @click="showDeleteConfirmation" class="delete-btn">
                Delete User
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
        <div class="modal-content delete-modal" @click.stop>
          <div class="modal-header">
            <h2>Confirm Delete</h2>
            <button
              @click="cancelDelete"
              class="close-btn"
              aria-label="Close modal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="delete-warning">
              <strong>Warning:</strong> This action cannot be undone.
            </p>
            <p>
              Are you sure you want to delete the user
              <strong
                >{{ userToDelete?.firstName }}
                {{ userToDelete?.lastName }}</strong
              >
              ({{ userToDelete?.email }})?
            </p>
            <p>This will:</p>
            <ul class="delete-actions">
              <li>Delete all user data from the database</li>
              <li>Remove survey responses</li>
              <li>Delete the user account</li>
            </ul>
          </div>
          <div class="modal-footer">
            <button @click="cancelDelete" class="btn btn-secondary">
              Cancel
            </button>
            <button
              @click="confirmDelete"
              class="btn btn-danger"
              :disabled="deletingUser"
            >
              {{ deletingUser ? "Deleting..." : "Delete User" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Survey User Confirmation Modal -->
      <div
        v-if="showDeleteSurveyModal"
        class="modal-overlay"
        @click="cancelDeleteSurveyUser"
      >
        <div class="modal-content delete-modal" @click.stop>
          <div class="modal-header">
            <h2>Confirm Delete Survey Response</h2>
            <button
              @click="cancelDeleteSurveyUser"
              class="close-btn"
              aria-label="Close modal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="delete-warning">
              <strong>Warning:</strong> This action cannot be undone.
            </p>
            <p>
              Are you sure you want to delete the survey response for
              <strong
                >{{ surveyUserToDelete?.firstName }}
                {{ surveyUserToDelete?.lastName }}</strong
              >
              ({{ surveyUserToDelete?.email }})?
            </p>
            <p>This will:</p>
            <ul class="delete-actions">
              <li>Delete the survey response data</li>
            </ul>
          </div>
          <div class="modal-footer">
            <button @click="cancelDeleteSurveyUser" class="btn btn-secondary">
              Cancel
            </button>
            <button
              @click="confirmDeleteSurveyUser"
              class="btn btn-danger"
              :disabled="deletingSurveyUser"
            >
              {{
                deletingSurveyUser ? "Deleting..." : "Delete Survey Response"
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner">Loading...</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db, auth } from "@/config/firebase";
import { AdminService } from "@/services/adminService";
import { AuthService } from "@/services/authService";
import UserProfileDropdown from "@/components/UserProfileDropdown.vue";
import { getDummySurveyResponses } from "@/stores/survey";

const users = ref([]);
const loading = ref(false);
const searchQuery = ref("");
const statusFilter = ref("");
const dateFilter = ref("");
const sortField = ref("lastUpdated");
const sortDirection = ref("desc");
const selectedUser = ref(null);

// Pagination state
const currentPage = ref(1);
const pageSize = 10;

// Admin authentication
const isAuthenticated = ref(false);
const currentAdmin = ref(null);
const adminEmail = ref("");
const isLoading = ref(false);
const isReturningFromLogin = ref(false);
const message = ref("");
const messageType = ref("");
const notAuthorized = ref(false);

// NEW: Track initial dashboard loading
const initialDashboardLoading = ref(false);
let initialLoadDone = false;

// Modal state
const showDeleteModal = ref(false);
const userToDelete = ref(null);
const deletingUser = ref(false);
const showDeleteSurveyModal = ref(false);
const surveyUserToDelete = ref(null);
const deletingSurveyUser = ref(false);

// Tab state
const activeTab = ref("survey");

// Firebase Auth users state
const authUsers = ref([]);
const authLoading = ref(false);

// Admin management state
const adminEmails = ref([]);
const adminLoading = ref(false);
const adminMessage = ref("");
const adminMessageType = ref("");

// Toggle slider state
const tabNames = ["survey", "auth", "admins", "issues"];
const activeTabIndex = computed(() => tabNames.indexOf(activeTab.value));
const sliderStyle = computed(() => ({
  transform: `translateX(${activeTabIndex.value * 100}%)`,
  width: "25%",
}));

// Function to handle tab click and update slider position
const handleTabClick = (tabName) => {
  activeTab.value = tabName;
};

onMounted(async () => {
  // Email link handling is now done at the app level in main.js
  // to prevent router guard race conditions

  // Check if returning from login link
  const urlParams = new URLSearchParams(window.location.search);
  const isEmailLink = urlParams.has("apiKey") || urlParams.has("oobCode");
  if (isEmailLink) {
    isReturningFromLogin.value = true;
  }

  // Listen for auth state changes
  auth.onAuthStateChanged(async (user) => {
    if (user) {
      const isAdmin = await AdminService.isAdmin();
      if (isAdmin) {
        isAuthenticated.value = true;
        currentAdmin.value = user;
        notAuthorized.value = false;

        // Clear returning from login state after successful auth
        if (isReturningFromLogin.value) {
          setTimeout(() => {
            isReturningFromLogin.value = false;
          }, 2000); // Show welcome message for 2 seconds
        }

        // Use new initial dashboard loading logic
        if (!initialLoadDone) {
          await loadInitialDashboardData();
        } else {
          loadUsers();
          if (activeTab.value === "auth") {
            loadAuthUsers();
          }
          await loadAdminEmails();
        }
      } else {
        isAuthenticated.value = false;
        currentAdmin.value = null;
        users.value = [];
        authUsers.value = [];
        isReturningFromLogin.value = false;
        notAuthorized.value = true;
      }
    } else {
      isAuthenticated.value = false;
      currentAdmin.value = null;
      users.value = [];
      authUsers.value = [];
      isReturningFromLogin.value = false;
      notAuthorized.value = false;
    }
  });

  // TEMP: Load 55 dummy responses for pagination testing
  users.value = getDummySurveyResponses();
  // Always load issues on mount as well
  if (isAuthenticated.value) {
    await loadIssues();
  }

  // Always reset to the first tab on load
  activeTab.value = "survey";
});

// Watch for tab changes to load auth users when needed
watch(activeTab, (newTab) => {
  if (
    newTab === "auth" &&
    isAuthenticated.value &&
    authUsers.value.length === 0
  ) {
    loadAuthUsers();
  }
  if (
    newTab === "admins" &&
    isAuthenticated.value &&
    adminEmails.value.length === 0
  ) {
    loadAdminEmails();
  }
  if (
    newTab === "issues" &&
    isAuthenticated.value &&
    issues.value.length === 0
  ) {
    loadIssues();
  }
});

// Computed properties
const filteredUsers = computed(() => {
  let filtered = users.value;

  // Exclude admins from users list
  if (adminEmails.value.length > 0) {
    filtered = filtered.filter(
      (user) => !adminEmails.value.includes(user.email)
    );
  }

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
    );
  }

  // Status filter
  if (statusFilter.value) {
    filtered = filtered.filter((user) => {
      const status = getSurveyStatus(user);
      return status.class === statusFilter.value;
    });
  }

  // Date filter
  if (dateFilter.value) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      today.getDate()
    );

    filtered = filtered.filter((user) => {
      const userDate = new Date(
        user.createdAt?.seconds * 1000 || user.createdAt
      );
      switch (dateFilter.value) {
        case "today":
          return userDate >= today;
        case "week":
          return userDate >= weekAgo;
        case "month":
          return userDate >= monthAgo;
        default:
          return true;
      }
    });
  }

  // Sort
  filtered.sort((a, b) => {
    let aValue = a[sortField.value];
    let bValue = b[sortField.value];

    // Handle date fields
    if (sortField.value === "createdAt" || sortField.value === "lastUpdated") {
      aValue = new Date(
        aValue?.seconds * 1000 ||
          aValue ||
          a.createdAt?.seconds * 1000 ||
          a.createdAt
      );
      bValue = new Date(
        bValue?.seconds * 1000 ||
          bValue ||
          b.createdAt?.seconds * 1000 ||
          b.createdAt
      );
    }

    // Handle name sorting
    if (sortField.value === "firstName") {
      aValue = `${a.firstName} ${a.lastName}`;
      bValue = `${b.firstName} ${b.lastName}`;
    }

    if (sortDirection.value === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return filtered;
});

const totalPages = computed(() =>
  Math.ceil(filteredUsers.value.length / pageSize)
);
const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredUsers.value.slice(start, start + pageSize);
});

function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
}

watch(filteredUsers, () => {
  // Reset to first page if filter changes and current page is out of range
  if (currentPage.value > totalPages.value) {
    currentPage.value = 1;
  }
});

const completedSurveys = computed(() => {
  return users.value.filter(
    (user) => getSurveyStatus(user).class === "completed"
  ).length;
});

const partialSurveys = computed(() => {
  return users.value.filter((user) => getSurveyStatus(user).class === "partial")
    .length;
});

const todaySignups = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return users.value.filter((user) => {
    const userDate = new Date(user.createdAt?.seconds * 1000 || user.createdAt);
    return userDate >= today;
  }).length;
});

// Firebase Auth users computed properties
const emailVerifiedCount = computed(() => {
  return authUsers.value.filter((user) => user.emailVerified).length;
});

const disabledUsersCount = computed(() => {
  return authUsers.value.filter((user) => user.disabled).length;
});

const recentSignupsCount = computed(() => {
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return authUsers.value.filter((user) => {
    const creationTime = new Date(user.metadata.creationTime);
    return creationTime >= weekAgo;
  }).length;
});

// Add a computed property for filtered auth users
const filteredAuthUsers = computed(() => {
  let filtered = authUsers.value;
  if (adminEmails.value.length > 0) {
    filtered = filtered.filter(
      (user) => !adminEmails.value.includes(user.email)
    );
  }
  return filtered;
});

// Methods
const loadUsers = async () => {
  if (!isAuthenticated.value) {
    return;
  }

  // Double-check admin status
  const isAdmin = await AdminService.isAdmin();
  if (!isAdmin) {
    showError("You must be logged in as an admin to view this data.");
    return;
  }

  loading.value = true;
  try {
    const usersRef = collection(db, "users");
    const q = query(usersRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    const usersData = [];
    querySnapshot.forEach((doc) => {
      usersData.push({
        uid: doc.id,
        ...doc.data(),
      });
    });

    users.value = usersData;
  } catch (error) {
    console.error("Error loading users:", error);

    // Show user-friendly error message
    if (error.code === "permission-denied") {
      showError(
        "Permission denied. Please ensure you are logged in as an admin user."
      );
    } else {
      showError(`Error loading users: ${error.message}`);
    }
  } finally {
    loading.value = false;
  }
};

const getSurveyStatus = (user) => {
  if (!user.surveyData) {
    return { text: "No Survey", class: "new" };
  }

  // Check if survey is completed (has all required fields)
  const requiredFields = [
    "firstName",
    "lastName",
    "email",
    "primaryLocation",
    "currentRole",
  ];
  const hasRequiredFields = requiredFields.every(
    (field) => user.surveyData[field]
  );

  if (hasRequiredFields) {
    return { text: "Completed", class: "completed" };
  } else {
    return { text: "Partial", class: "partial" };
  }
};

const formatDate = (date) => {
  if (!date) return "N/A";

  const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatFieldName = (fieldName) => {
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

const formatFieldValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value, null, 2);
  }
  return value || "N/A";
};

const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

const viewUserDetails = (user) => {
  selectedUser.value = user;
};

const closeModal = () => {
  selectedUser.value = null;
};

const exportToCSV = () => {
  if (activeTab.value === "survey") {
    // Export survey users
    const headers = [
      "Name",
      "Email",
      "Signup Date",
      "Survey Status",
      "Last Activity",
    ];
    const csvContent = [
      headers.join(","),
      ...filteredUsers.value.map((user) =>
        [
          `"${user.firstName} ${user.lastName}"`,
          `"${user.email}"`,
          `"${formatDate(user.createdAt)}"`,
          `"${getSurveyStatus(user).text}"`,
          `"${formatDate(user.lastUpdated || user.createdAt)}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `survey-users-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  } else if (activeTab.value === "auth") {
    // Export Firebase auth users
    const headers = [
      "Email",
      "Email Verified",
      "Created At",
      "Last Sign In",
      "Status",
    ];
    const csvContent = [
      headers.join(","),
      ...authUsers.value.map((authUser) =>
        [
          `"${authUser.email}"`,
          `"${authUser.emailVerified ? "Verified" : "Unverified"}"`,
          `"${formatDate(authUser.metadata.creationTime)}"`,
          `"${formatDate(authUser.metadata.lastSignInTime)}"`,
          `"${authUser.disabled ? "Disabled" : "Active"}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `firebase-auth-users-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
};

// Admin authentication methods
const handleAdminLogin = async () => {
  if (!adminEmail.value) return;

  isLoading.value = true;
  message.value = "";

  try {
    // Check if user is already authenticated
    if (isAuthenticated.value && currentAdmin.value) {
      showMessage("You are already logged in as an admin.", "info");
      isLoading.value = false;
      return;
    }

    // Use the existing AuthService to send login link
    const result = await AuthService.sendLoginLink(
      adminEmail.value,
      "Admin",
      "User",
      "/surveyadmin"
    );

    if (result.success) {
      showMessage("Admin login link sent! Check your email.", "success");
    } else {
      showMessage(result.message, "error");
    }
  } catch (error) {
    showMessage("An error occurred. Please try again.", "error");
  } finally {
    isLoading.value = false;
  }
};

const handleSignOut = async () => {
  try {
    await auth.signOut();
    isAuthenticated.value = false;
    currentAdmin.value = null;
    users.value = [];
    showMessage("Successfully signed out!", "success");
  } catch (error) {
    showMessage("Error signing out. Please try again.", "error");
  }
};

const handleProfile = () => {
  // For now, just show an alert. This could be expanded to navigate to a profile page
  alert("Admin Profile functionality coming soon!");
};

const handleSettings = () => {
  // For now, just show an alert. This could be expanded to navigate to a settings page
  alert("Admin Settings functionality coming soon!");
};

const handleHelp = () => {
  // For now, just show an alert. This could be expanded to navigate to a help page
  alert("Admin Help functionality coming soon!");
};

const showMessage = (text, type) => {
  message.value = text;
  messageType.value = type;
  // Clear message after 5 seconds
  setTimeout(() => {
    message.value = "";
    messageType.value = "";
  }, 5000);
};

const showError = (text) => {
  showMessage(text, "error");
};

const showDeleteConfirmation = () => {
  userToDelete.value = selectedUser.value;
  showDeleteModal.value = true;
};

const cancelDelete = () => {
  userToDelete.value = null;
  showDeleteModal.value = false;
};

const confirmDelete = async () => {
  if (!userToDelete.value) return;

  deletingUser.value = true;
  try {
    // Check if it's an auth user or survey user based on the user object structure
    const isAuthUser = userToDelete.value.metadata;
    const result = isAuthUser
      ? await AdminService.deleteUserByAdmin(userToDelete.value.uid)
      : await AdminService.deleteUser(
          userToDelete.value.uid,
          userToDelete.value.email
        );

    if (result.success) {
      showMessage(result.message, "success");
      // Remove from appropriate lists
      if (isAuthUser) {
        authUsers.value = authUsers.value.filter(
          (user) => user.uid !== userToDelete.value.uid
        );
        users.value = users.value.filter(
          (user) => user.uid !== userToDelete.value.uid
        );
      } else {
        users.value = users.value.filter(
          (user) => user.uid !== userToDelete.value.uid
        );
      }
      closeModal(); // Close the user details modal after successful deletion
    } else {
      showError(result.message);
    }
  } catch (error) {
    showError(`Error deleting user: ${error.message}`);
  } finally {
    userToDelete.value = null;
    showDeleteModal.value = false;
    deletingUser.value = false;
  }
};

// Firebase Auth users methods
const loadAuthUsers = async () => {
  authLoading.value = true;
  try {
    const result = await AdminService.listAuthUsers();
    if (result.success) {
      authUsers.value = result.users;
    } else {
      showError(result.message);
    }
  } catch (error) {
    showError(`Error loading auth users: ${error.message}`);
  } finally {
    authLoading.value = false;
  }
};

const deleteAuthUser = async (authUser) => {
  userToDelete.value = authUser;
  showDeleteModal.value = true;
};

const deleteSurveyUser = async (user) => {
  surveyUserToDelete.value = user;
  showDeleteSurveyModal.value = true;
};

const confirmDeleteSurveyUser = async () => {
  if (!surveyUserToDelete.value) return;

  deletingSurveyUser.value = true;
  try {
    const result = await AdminService.deleteUser(
      surveyUserToDelete.value.uid,
      surveyUserToDelete.value.email
    );
    if (result.success) {
      showMessage(result.message, "success");
      // Remove from survey users list
      users.value = users.value.filter(
        (u) => u.uid !== surveyUserToDelete.value.uid
      );
    } else {
      showError(result.message);
    }
  } catch (error) {
    showError(`Error deleting survey user: ${error.message}`);
  } finally {
    surveyUserToDelete.value = null;
    showDeleteSurveyModal.value = false;
    deletingSurveyUser.value = false;
  }
};

const cancelDeleteSurveyUser = () => {
  surveyUserToDelete.value = null;
  showDeleteSurveyModal.value = false;
};

// Admin management methods
const loadAdminEmails = async () => {
  adminLoading.value = true;
  try {
    const result = await AdminService.getAdminEmails();
    if (result.success) {
      adminEmails.value = result.adminEmails;
    } else {
      showAdminMessage(result.message, "error");
    }
  } catch (error) {
    showAdminMessage(`Error loading admin emails: ${error.message}`, "error");
  } finally {
    adminLoading.value = false;
  }
};

const showAdminMessage = (text, type) => {
  adminMessage.value = text;
  adminMessageType.value = type;
  // Clear message after 5 seconds
  setTimeout(() => {
    adminMessage.value = "";
    adminMessageType.value = "";
  }, 5000);
};

// --- Initial Dashboard Loading Logic ---
async function loadInitialDashboardData() {
  initialDashboardLoading.value = true;
  await Promise.all([
    loadUsers(),
    loadAuthUsers(),
    loadAdminEmails(),
    loadIssues(),
  ]);
  initialDashboardLoading.value = false;
  initialLoadDone = true;
}

const issues = ref([]);
const issuesLoading = ref(false);
const selectedIssue = ref(null);

const loadIssues = async () => {
  issuesLoading.value = true;
  try {
    const issuesRef = collection(db, "issues");
    const q = query(issuesRef, orderBy("debugInfo.timestamp", "desc"));
    const querySnapshot = await getDocs(q);
    const issuesData = [];
    querySnapshot.forEach((doc) => {
      issuesData.push({ id: doc.id, ...doc.data() });
    });
    issues.value = issuesData;
  } catch (error) {
    console.error("Error loading issues:", error);
  } finally {
    issuesLoading.value = false;
  }
};

const viewIssueDetails = (issue) => {
  selectedIssue.value = issue;
};
const closeIssueModal = () => {
  selectedIssue.value = null;
};

function formatDebugInfo(debugInfo) {
  if (!debugInfo) return "No debug info.";
  try {
    return JSON.stringify(debugInfo, null, 2);
  } catch {
    return String(debugInfo);
  }
}

function getIssuePage(issue) {
  // Prefer route, fallback to path, fallback to N/A
  return (
    issue.debugInfo?.route ||
    issue.debugInfo?.path ||
    issue.debugInfo?.url ||
    "N/A"
  );
}

const selectedIssueIds = ref([]);

const toggleIssueSelection = (issueId) => {
  if (selectedIssueIds.value.includes(issueId)) {
    selectedIssueIds.value = selectedIssueIds.value.filter(
      (id) => id !== issueId
    );
  } else {
    selectedIssueIds.value.push(issueId);
  }
};

const selectAllIssues = (event) => {
  if (event.target.checked) {
    selectedIssueIds.value = issues.value.map((issue) => issue.id);
  } else {
    selectedIssueIds.value = [];
  }
};

const deleteSelectedIssues = async () => {
  if (selectedIssueIds.value.length === 0) return;
  if (
    !confirm(
      `Are you sure you want to delete ${selectedIssueIds.value.length} selected issue(s)? This cannot be undone.`
    )
  )
    return;
  for (const issueId of selectedIssueIds.value) {
    try {
      await deleteDoc(doc(db, "issues", issueId));
      issues.value = issues.value.filter((issue) => issue.id !== issueId);
    } catch (error) {
      console.error("Error deleting issue:", error);
    }
  }
  selectedIssueIds.value = [];
};
</script>

<style scoped>
.admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  background: #f4f6fa; /* subtle light grey for the whole admin area */
  min-height: 100vh;
}

/* Admin Login Styles */
.admin-login {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
}

.login-card {
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
}

.login-card h1 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  text-align: center;
}

.login-card p {
  text-align: center;
  color: #6c757d;
  margin-bottom: 2rem;
}

.login-form {
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #2c3e50;
}

.login-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
}

.login-input:focus {
  outline: none;
  border-color: #005461;
  box-shadow: 0 0 0 2px rgba(0, 84, 97, 0.2);
}

.login-btn {
  width: 100%;
  padding: 0.75rem;
  background-color: #005461;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.login-btn:hover:not(:disabled) {
  background-color: #004a56;
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.message {
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  text-align: center;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.admin-info {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 6px;
}

.admin-info h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1rem;
}

.admin-info ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #6c757d;
}

.admin-info li {
  margin-bottom: 0.5rem;
}

/* Admin Dashboard Styles */
.admin-dashboard {
  width: 100%;
  padding: 2.5rem 2rem 2rem 2rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e1e5e9;
  background: transparent;
  border-radius: 12px 12px 0 0;
}

.admin-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
}

.admin-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.admin-user {
  color: #6c757d;
  font-weight: 500;
  margin-right: 1rem;
}

.refresh-btn,
.export-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn {
  background-color: #005461;
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #004a56;
}

.export-btn {
  background-color: #6c757d;
  color: white;
}

.export-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.refresh-btn:disabled,
.export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: #fff;
  padding: 2rem 1.5rem;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(60, 72, 88, 0.06);
  text-align: center;
  transition: box-shadow 0.2s;
}

.stat-card:hover {
  box-shadow: 0 6px 24px rgba(60, 72, 88, 0.12);
}

.stat-card h3 {
  margin: 0 0 0.5rem 0;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
}

.stat-number {
  margin: 0;
  font-size: 2rem;
  font-weight: bold;
  color: #2c3e50;
}

.search-filter-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

.filter-controls {
  display: flex;
  gap: 1rem;
}

.filter-select {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background: white;
}

.table-container {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(60, 72, 88, 0.06);
  overflow: hidden;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table th,
.users-table td {
  padding: 1.1rem 1rem;
  text-align: left;
  border-bottom: 1px solid #e1e5e9;
}

.users-table th {
  background-color: #f4f6fa;
  font-weight: 700;
  color: #495057;
  font-size: 1rem;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef;
}

.sort-indicator {
  margin-left: 0.5rem;
  color: #005461;
}

.user-row:hover {
  background-color: #f0f2f7;
}

.user-name {
  font-weight: 500;
  color: #2c3e50;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.completed {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.partial {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.new {
  background-color: #d1ecf1;
  color: #0c5460;
}

.view-btn {
  padding: 0.5rem 1rem;
  background-color: #005461;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}

.view-btn:hover {
  background-color: #004a56;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e1e5e9;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  margin: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  color: #495057;
  background-color: #f8f9fa;
}

.close-btn span {
  line-height: 1;
  font-weight: 300;
}

.modal-body {
  padding: 1.5rem;
}

/* Error Modal Styles */
.error-modal {
  max-width: 500px;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e1e5e9;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Delete Modal Styles */
.delete-modal {
  max-width: 500px;
}

.delete-warning {
  color: #dc3545;
  font-weight: 500;
  margin-bottom: 1rem;
}

.delete-actions {
  margin: 1rem 0;
  padding-left: 1.5rem;
  color: #495057;
}

.delete-actions li {
  margin-bottom: 0.5rem;
}

/* Modal Actions */
.modal-actions {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e1e5e9;
  display: flex;
  justify-content: flex-end;
}

.delete-btn {
  padding: 0.75rem 1.5rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.delete-btn:hover {
  background-color: #c82333;
}

.user-info,
.survey-data {
  margin-bottom: 2rem;
}

.user-info h3,
.survey-data h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e1e5e9;
}

.survey-sections {
  display: grid;
  gap: 1rem;
}

.survey-section {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
}

.survey-section h4 {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 600;
}

.survey-section p {
  margin: 0;
  color: #2c3e50;
  word-break: break-word;
}

.no-survey-data {
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

/* Loading State */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  backdrop-filter: blur(4px);
}

.loading-content {
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
  width: 90%;
}

.loading-content h3 {
  margin: 1rem 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.loading-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.95rem;
}

.loading-spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid #e9ecef;
  border-top: 4px solid #005461;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-spinner {
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-weight: 500;
  color: #2c3e50;
}

.loading-spinner-small {
  padding: 0.5rem;
  font-weight: 500;
  color: #6c757d;
  font-size: 0.9rem;
  text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .admin-container {
    padding: 1rem;
  }

  .login-card {
    padding: 2rem;
  }

  .admin-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .admin-controls {
    flex-direction: column;
    width: 100%;
  }

  .search-filter-section {
    flex-direction: column;
  }

  .search-box {
    min-width: auto;
  }

  .filter-controls {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .users-table {
    font-size: 0.9rem;
  }

  .users-table th,
  .users-table td {
    padding: 0.75rem 0.5rem;
  }

  .tab-btn {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
  }
}

/* Tab Navigation Styles with Animated Toggle */
.tab-navigation {
  display: flex;
  gap: 0;
  margin-bottom: 2rem;
  justify-content: flex-start;
  align-items: flex-end;
  text-align: left;
  padding-left: 0;
  margin-left: 0;
  width: 100%;
  box-sizing: border-box;
  background: transparent;
}

.tab-toggle-container {
  position: relative;
  display: flex;
  background: #f4f6fa;
  border-radius: 12px;
  padding: 4px;
  border: 1px solid #e1e5e9;
  box-shadow: 0 2px 8px rgba(60, 72, 88, 0.04);
  width: 100%;
}

.tab-toggle-slider {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 0;
  background: #005461;
  border-radius: 8px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 84, 97, 0.2);
  min-width: 40px;
  opacity: 1;
  width: 25%;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  width: 100%;
}

.tab-title {
  text-align: left;
}

.tab-title h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
}

.tab-actions {
  flex-shrink: 0;
}

.tab-btn {
  flex: 1 1 0;
  width: 25%;
  padding: 1rem 1.5rem;
  background: transparent;
  border: none;
  font-size: 1.05rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
  text-align: center;
  position: relative;
  z-index: 2;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-btn:hover {
  color: #2c3e50;
}

.tab-btn.active {
  color: #ffffff;
  font-weight: 700;
}

.tab-btn:focus {
  outline: none;
  box-shadow: none;
}

.tab-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

.tab-content {
  width: 100%;
  padding-right: 0;
}

/* Firebase Auth Users Styles */
.auth-users-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 2rem;
}

.auth-users-header h2 {
  margin: 0;
  color: #2c3e50;
}

.auth-users-header .refresh-btn {
  margin-left: auto !important;
  margin-right: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.uid-text {
  font-family: monospace;
  font-size: 0.9rem;
  color: #6c757d;
}

.delete-btn-small {
  padding: 0.25rem 0.75rem;
  background-color: #ea5d0a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s ease;
  width: 80px;
  min-width: 80px;
}

.delete-btn-small:hover {
  background-color: #d45409;
}

.no-data {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  font-style: italic;
}

/* Additional Status Badge Styles */
.status-badge.verified {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.unverified {
  background-color: #f8d7da;
  color: #721c24;
}

.status-badge.active {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.disabled {
  background-color: #f8d7da;
  color: #721c24;
}

/* Modern Admin Management Styles */
.admin-management-modern {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 0;
  width: 100%;
}
.admin-input-modern {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
}
.admin-input-modern:focus {
  outline: none;
  border-color: #005461;
  box-shadow: 0 0 0 2px rgba(0, 84, 97, 0.2);
}
.add-admin-btn-modern {
  padding: 0.75rem 1.5rem;
  background-color: #005461;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.add-admin-btn-modern:hover:not(:disabled) {
  background-color: #004a56;
}
.add-admin-btn-modern:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.remove-admin-btn-modern {
  padding: 0.25rem 0.75rem;
  background-color: #ea5d0a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s ease;
  width: 80px;
  min-width: 80px;
}

/* Ensure Actions column is right-aligned */
.users-table th:last-child,
.users-table td:last-child {
  text-align: right;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.remove-admin-btn-modern:hover:not(:disabled) {
  background-color: #d45409;
}
.remove-admin-btn-modern:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
@media (max-width: 768px) {
  .admin-management-card {
    padding: 1.2rem 0.5rem;
  }
  .add-admin-form-modern {
    flex-direction: column;
    align-items: stretch;
  }
  .admin-item-modern {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
}

/* Hide nav until ready for slider calculation */
.nav-hidden {
  visibility: hidden !important;
  opacity: 0 !important;
}

/* Issue Details Modal Row Layout */
.issue-details-row {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
}
.issue-detail-col {
  flex: 1 1 0;
  min-width: 180px;
  max-width: 33%;
  box-sizing: border-box;
}
.debug-info-pre {
  background: #f4f6fa;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.95em;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  /* Set a max-height and make only this scrollable */
  overflow: auto;
  height: 100%;
}

.issue-debug-full {
  width: 100%;
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  height: 90%;
  /* Remove height: 100%; so it doesn't force the pre to fill modal */
}
.issue-debug-full h3 {
  margin-bottom: 0.5rem;
}

.issue-modal-content {
  max-width: 1200px;
  width: 98vw;
  height: 95vh;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
}
.issue-modal-content .modal-body {
  flex: 1 1 auto;
  /* Remove overflow-y: auto; so the main modal doesn't scroll */
  overflow: visible;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.issue-modal-close-btn {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 10;
}
</style>
